import { Menu, MenuItem, ListItemIcon } from '@mui/material';
import { parse, ParseResult } from 'papaparse';
import React, { useState } from 'react';
import ProgressBreadcrumbs from '@/components/ProgressBreadcrumbs/ProgressBreadcrumbs';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import {
  Stack,
  Text,
  Box,
  InputLabel,
  Input,
  Button,
} from '@/theme/components';
import { EditListIcon } from '@/theme/customIcons/EditListIcon';
import { ImportListIcon } from '@/theme/customIcons/ImportListIcon';
import { UploadSuccessIcon } from '@/theme/customIcons/UploadSuccessIcon';
import { Icons, styled, Divider } from '@/theme/index';
import { nameize } from '@/utils/nameize';
import {
  MappingTable,
  Spinner,
  ParsedData,
  ImportNewListForm,
} from './components';
import { useAccountLists } from '../AccountLists/useAccountLists/useAccountLists';
import { useAccountListsV2 } from '../AccountListsV2/hooks/useAccountListsV2';

export type ImportListState = {
  error: string;
  currentStep: number;
  loading: boolean;
  loadingMessage: string;
  listName: string;
  listMenuOpen: boolean;
  formOpen: boolean;
  data: ParsedData[];
  headers: string[];
  mapping: { [key: string]: string };
};

type MutateError = {
  error?: string;
};

const DEFAULT_MAAPING = {
  'street address': 'street',
  city: 'city',
  state: 'state',
  zip: 'zip5',
};

const DEFAULT_STATE = {
  error: '',
  currentStep: 1,
  loading: false,
  loadingMessage: '',
  listName: '',
  listMenuOpen: false,
  formOpen: false,
  data: [],
  headers: [],
  mapping: DEFAULT_MAAPING,
};

const STEPS = {
  1: 'Upload File',
  2: 'Map Fields',
  3: 'Choose List',
  4: 'Import List',
};

const LIST_PROPERTY_PARAMS = [
  'street',
  'city',
  'unit',
  'state',
  'zip5',
  'county',
  'apn',
  'owner_occupied',
  'beds',
  'baths',
  'sqft',
  'property_type',
  'lot_size',
  'estimated_value',
  'assessed_value',
  'land_value',
  'improvement_value',
  'last_date_sold',
  'last_sold_price',
  'year_built',
  'year_built_effective',
  'last_transfer_date',
  'permits',
  'tax_year',
  'tax_value',
  'latitude',
  'longitude',
];

const DropZone = styled(Stack)(({ theme }) => ({
  width: '100%',
  minHeight: '400px',
  borderRadius: '4px',
  border: `1px solid ${
    theme.palette.progress?.contrastText || theme.palette.secondary.contrastText
  }`,
  fontSize: '100px',
}));

const FileReaderInput = styled(Input)`
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
`;

const FileReaderInputLabel = styled(InputLabel)`
  text-decoration: underline;
  cursor: pointer;
`;

const ErrorBox = styled(Stack)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.error.main,
  padding: '5px',
  borderRadius: '4px',
}));

const FileReaderFalseButton = styled(InputLabel)(({ theme }) => ({
  width: '172px',
  height: '42px',
  borderRadius: '4px',
  border: 'none',
  color: `${theme.palette.primary.contrastText}`,
  backgroundColor: `${theme.palette.primary.main}`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
}));

const EditListIconBackground = styled(Stack)(({ theme }) => ({
  width: '92px',
  height: '92px',
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.light,
  fontSize: '50px',
}));

const AddToListButtonContainer = styled(Stack)(({ theme }) => ({
  padding: '24px',
  border: `1px solid ${
    theme.palette.progress?.contrastText || theme.palette.secondary.contrastText
  }`,
}));

const AddToListButton = styled(Button)(({ theme }) => ({
  height: '44px',
  width: '305px',
  color: theme.palette.progress?.main || theme.palette.primary.dark,
  backgroundColor: theme.palette.info.light,
}));

export function ImportList() {
  const {
    data: { brandName },
  } = useAppConstantsContext();
  const { navigate } = useAppConfigContext().routingUtils;
  const [state, setState] = useState<ImportListState>(DEFAULT_STATE);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { data: accountLists, createList } = useAccountLists();
  const { bulkAddListProperty } = useAccountListsV2();

  const handleAddToListButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setState({ ...state, listMenuOpen: true });
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setState({ ...state, listMenuOpen: false });
    setAnchorEl(null);
  };

  const handleAddToNewListLinkClick = () => {
    setState({ ...state, formOpen: true });
  };

  const handleCloseForm = () => {
    setState({ ...state, formOpen: false });
  };

  const setError = (error: string) => {
    setState({ ...state, error, loading: false });
  };

  const clearError = () => {
    setState({ ...state, error: '' });
  };

  const setListName = (name: string) => {
    setState({ ...state, listName: name });
  };

  const setLoadingMessage = (message: string) => {
    setState({
      ...state,
      loading: true,
      loadingMessage: message,
    });
  };

  const validateFields = (results: ParseResult<ParsedData>) => {
    if (!results?.meta?.fields) {
      setError('There was and error processing your file, Please try again.');
      return;
    }

    setState({
      ...state,
      data: results?.data,
      headers: results?.meta?.fields,
      currentStep: 2,
      loading: false,
      error: '',
    });
  };

  const processFile = (file: File | null | undefined) => {
    if (file?.type === 'text/csv') {
      clearError();
      setLoadingMessage('Uploading');
      parse<ParsedData>(file, {
        header: true,
        transformHeader: (header: string) => {
          return header.trim().toLowerCase();
        },
        complete: (results: ParseResult<ParsedData>) => {
          validateFields(results);
        },
        error: (error) => {
          setError(error.message);
        },
      });
    } else {
      setError('Invalid file type. Please upload a CSV file to continue.');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    // Prevent default behavior (Prevent file from being opened)
    e.preventDefault();

    if (e?.dataTransfer?.items) {
      // Use DataTransferItemList interface to access the file(s)
      const item = e.dataTransfer.items[0];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        processFile(file);
      }
    } else {
      // Use DataTransfer interface to access the file(s)
      const file = e?.dataTransfer?.files[0];
      processFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    // Prevent default behavior (Prevent file from being opened)
    e.preventDefault();
  };

  const handleFileInput = (e: React.ChangeEvent) => {
    e.preventDefault();
    const file = (e?.currentTarget as HTMLInputElement)?.files?.[0];
    processFile(file);
  };

  const setMapping = (key: string, value: string) => {
    if (key && value) {
      const newMapping = { ...state.mapping };
      newMapping[key] = value;
      setState({ ...state, mapping: newMapping });
    }
  };

  const resetComponent = () => {
    setState(DEFAULT_STATE);
  };

  const continueUpload = () => {
    if (state.data && state.mapping) {
      const newData = [...state.data];
      for (let index = 0; index < newData.length; index++) {
        const element = newData[index];
        const keys = Object.keys(element);
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i];
          if (state.mapping[key] && key !== state.mapping[key]) {
            element[state.mapping[key]] = element[key];
          }
        }
      }

      if (
        !newData?.[0]?.street ||
        !newData?.[0]?.city ||
        !newData?.[0]?.state ||
        !newData?.[0]?.zip5
      ) {
        setError(
          'You need to have street, city, state, zip5 mapped to continue.'
        );
        return;
      }

      setState({
        ...state,
        data: newData,
        currentStep: 3,
        error: '',
      });
    }
  };

  const addToExistingList = (listCode: string, listName: string) => () => {
    setState({
      ...state,
      loading: true,
      loadingMessage: 'Importing',
    });

    bulkAddListProperty.mutate(
      {
        list_code: listCode,
        properties: state.data,
      },
      {
        onSuccess: () => {
          setState({
            ...state,
            loading: false,
            currentStep: 4,
            listMenuOpen: false,
            listName,
          });
        },
        onError(error) {
          setState({
            ...state,
            loading: false,
            error: String((error as MutateError)?.error),
            listMenuOpen: false,
          });
        },
      }
    );
  };

  const addToNewList = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (state.loading && state.loadingMessage === 'Creating List') {
      return;
    }

    if (!state.listName) {
      setState({
        ...state,
        error: 'List name is required',
      });
      return;
    }

    const isNameValid = /^[0-9a-zA-Z\s]+$/.test(state.listName);
    if (!isNameValid) {
      setState({
        ...state,
        error: 'Special characters are not allowed',
      });
      return;
    }

    setState({
      ...state,
      loading: true,
      loadingMessage: 'Creating List',
    });

    createList.mutate(
      {
        name: state.listName,
        permalink: '',
      },
      {
        onSuccess: (response) => {
          if (response.data.list) {
            const { name, list_code } = response.data.list;
            setState({
              ...state,
              loading: true,
              loadingMessage: 'Importing',
              listName: name,
            });
            bulkAddListProperty.mutate(
              {
                list_code,
                properties: state.data,
              },
              {
                onSuccess: () => {
                  setState({
                    ...state,
                    loading: false,
                    currentStep: 4,
                  });
                },
                onError: (error) => {
                  setState({
                    ...state,
                    loading: false,
                    error: String((error as MutateError)?.error),
                    listMenuOpen: false,
                    formOpen: false,
                  });
                },
              }
            );
          }
        },
        onError: (error) => {
          setState({
            ...state,
            loading: false,
            error: String((error as MutateError)?.error),
            listMenuOpen: false,
            formOpen: false,
          });
        },
      }
    );
  };

  const renderContent = () => {
    if (state.loading) {
      return (
        <>
          <ProgressBreadcrumbs steps={STEPS} currentStep={state.currentStep} />
          <Stack alignItems="center">
            <Text variant="h1">{state.loadingMessage}</Text>
            <Spinner />
          </Stack>
        </>
      );
    }
    if (state.currentStep === 1) {
      return (
        <>
          <Text variant="h3">Import multiple addresses at once</Text>
          <Box>
            <Text>
              Import addresses you already have and build lists quickly to find
              owner and property details without manually searching each lead.
              To get started, download the CSV template or Import your own.
            </Text>
            <Text fontWeight="bold">
              Your file should include, at a minimum, a header row with an
              address.
            </Text>
          </Box>
          {state.error && (
            <ErrorBox direction="row" alignItems="center" spacing={1}>
              <Icons.ErrorOutline />
              <Text>{state.error}</Text>
            </ErrorBox>
          )}
          <DropZone
            alignItems="center"
            justifyContent="center"
            spacing={3}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <ImportListIcon fontSize="inherit" />
            <Text variant="h1">Drag & drop your file here</Text>
            <Stack direction="row" spacing={1} alignItems="center">
              <Text>Or</Text>
              <FileReaderInputLabel htmlFor="file">
                choose a file
              </FileReaderInputLabel>
              <Text>on your computer</Text>
            </Stack>
            <Text variant="h6" fontWeight="normal">
              Supported format: CSV
            </Text>
            <FileReaderInput
              type="file"
              name="file"
              id="file"
              onChange={handleFileInput}
            />
            <FileReaderFalseButton htmlFor="file">Upload</FileReaderFalseButton>
          </DropZone>
        </>
      );
    }
    if (state.currentStep === 2) {
      return (
        <>
          <ProgressBreadcrumbs steps={STEPS} currentStep={state.currentStep} />
          <Text variant="h3">Map your CSV file to {brandName}</Text>
          <Text>
            Once you submit your list, we’ll look up the property owners and add
            them to your account. We’ll send you an email once your upload is
            complete.
          </Text>
          {state.error && (
            <ErrorBox direction="row" alignItems="center" spacing={1}>
              <Icons.ErrorOutline />
              <Text>{state.error}</Text>
            </ErrorBox>
          )}
          <MappingTable
            data={state.data}
            headers={state.headers}
            fields={LIST_PROPERTY_PARAMS}
            mapping={state.mapping}
            setMapping={setMapping}
          />
          <Stack direction="row" justifyContent="space-between">
            <Button onClick={() => resetComponent()}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => continueUpload()}
            >
              Continue
            </Button>
          </Stack>
        </>
      );
    }
    if (state.currentStep === 3) {
      return (
        <>
          <ProgressBreadcrumbs steps={STEPS} currentStep={state.currentStep} />
          {state.error && (
            <ErrorBox direction="row" alignItems="center" spacing={1}>
              <Icons.ErrorOutline />
              <Text>{state.error}</Text>
            </ErrorBox>
          )}
          <DropZone alignItems="center" justifyContent="center" spacing={3}>
            <EditListIconBackground alignItems="center" justifyContent="center">
              <EditListIcon fontSize="inherit" />
            </EditListIconBackground>
            <Text variant="h6" fontWeight="normal">
              Almost done, name your list to complete your import.
            </Text>
            <AddToListButtonContainer direction="column" alignItems="center">
              <Text variant="h4">
                {state.data.length} rows are ready to be added
              </Text>
              <AddToListButton onClick={handleAddToListButtonClick}>
                <Icons.List />
                List
              </AddToListButton>
              <Menu
                id="import-list-menu"
                anchorEl={anchorEl}
                open={state.listMenuOpen}
                onClose={handleCloseMenu}
                MenuListProps={{
                  sx: { width: anchorEl && anchorEl.offsetWidth },
                }}
              >
                {state.formOpen ? (
                  <ImportNewListForm
                    listName={state.listName}
                    setListName={setListName}
                    handleCloseForm={handleCloseForm}
                    onSubmit={addToNewList}
                  />
                ) : (
                  <>
                    <MenuItem onClick={handleAddToNewListLinkClick}>
                      <ListItemIcon>
                        <Icons.Add fontSize="small" color="primary" />
                      </ListItemIcon>
                      <Text color="primary">Add to new List</Text>
                    </MenuItem>
                    <Divider sx={{ width: '87%', margin: '12px auto' }} />
                    {accountLists?.map((list) => (
                      <MenuItem
                        key={`import-list-add-to-list-menu-item-${list.id}`}
                        onClick={addToExistingList(list.list_code, list.name)}
                      >
                        <ListItemIcon>
                          <Icons.Add fontSize="small" color="primary" />
                        </ListItemIcon>
                        <Text color="primary">{nameize(list.name)}</Text>
                      </MenuItem>
                    ))}
                  </>
                )}
              </Menu>
            </AddToListButtonContainer>
          </DropZone>
        </>
      );
    }
    if (state.currentStep === 4) {
      return (
        <>
          <ProgressBreadcrumbs steps={STEPS} currentStep={state.currentStep} />
          <DropZone alignItems="center" justifyContent="center" spacing={3}>
            <UploadSuccessIcon fontSize="inherit" />
            <Text variant="h4">
              Your import was successfully added to {nameize(state.listName)}
            </Text>
            <Button
              variant="contained"
              color="success"
              onClick={() =>
                navigate(
                  `/dashboard/lists/${encodeURIComponent(
                    state.listName.toLowerCase()
                  )}`
                )
              }
            >
              View List
            </Button>
            <Button onClick={() => resetComponent()}>Upload a new list</Button>
          </DropZone>
        </>
      );
    }
    return null;
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1}>
        <Icons.CloudUpload color="primary" />
        <Text variant="h2">Bulk Import</Text>
      </Stack>
      {renderContent()}
    </Stack>
  );
}
